import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import ChallengeSection from '../components/sections/ChallengeSection';
import JourneySection from '../components/sections/JourneySection';
import UseCaseSection from '../components/sections/UseCaseSection';
import FeaturePreviewSection from '../components/sections/FeaturePreviewSection';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';
import residentsContent from '../content/residents/index.json';
import supportContent from '../content/support/index.json';
import * as Icons from 'lucide-react';
import { CheckCircle, XCircle, ArrowRight, Shield, MessageSquare } from 'lucide-react';

const getIcon = (name) => {
  const Icon = Icons[name];
  return Icon ? <Icon className="w-5 h-5" /> : null;
};

export default function ResidentsPage() {
  usePageMeta(
    'ResiAX for Residents — your housing record, your control',
    'Keep the history of your home, issues, evidence and communication connected in a record you control — with AI grounded in your context and support from people you trust.'
  );

  React.useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        document.querySelector(window.location.hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const d = residentsContent;
  const s = supportContent;

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {/* 1. Hero */}
        <section className="pt-32 pb-14 bg-white" data-testid="residents-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-4">
              <span className="w-8 h-px bg-[#B5541B]" />
              {d.hero.eyebrow}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F1E1E] leading-tight mb-6" data-testid="residents-heading">
              {d.hero.title}
            </h1>
            <p className="text-base md:text-lg text-[#3B3535]/75 leading-relaxed max-w-2xl mb-8">{d.hero.intro}</p>
            <Button
              asChild
              data-testid="residents-hero-cta"
              className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Link to="/resident-pilot#register">
                Join resident early access
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* 2. Full challenge framing */}
        <ChallengeSection />

        {/* 3. Four-step journey */}
        <JourneySection />

        {/* 4. Interactive use cases */}
        <UseCaseSection />

        {/* 5. Feature preview */}
        <FeaturePreviewSection />

        {/* 6. Resident-owned record */}
        <section id="your-record" className="py-16 lg:py-20 bg-white scroll-mt-20" data-testid="residents-record">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-4">{d.record.heading}</h2>
            {d.record.paragraphs.map((p, i) => (
              <p key={i} className="text-base text-[#3B3535]/80 leading-relaxed mb-4">{p}</p>
            ))}
            <ul className="grid sm:grid-cols-2 gap-3 mt-6">
              {d.record.points.map((point, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#3B3535]/85 bg-[#FFF8F5] rounded-xl p-4 border border-[#B5541B]/10">
                  <CheckCircle className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 7. Evidence-grounded AI */}
        <section id="context-ai" className="py-16 lg:py-20 bg-[#1F1E1E] scroll-mt-20" data-testid="residents-ai">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{d.ai.heading}</h2>
            <p className="text-[#D4722F] font-semibold mb-6">{d.ai.tagline}</p>
            {d.ai.paragraphs.map((p, i) => (
              <p key={i} className="text-white/75 leading-relaxed mb-4 max-w-3xl">{p}</p>
            ))}
            <div className="grid md:grid-cols-2 gap-5 mt-8 mb-6">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="ai-generic-panel">
                <p className="text-xs font-bold uppercase tracking-wide text-white/50 mb-4">{d.ai.genericLabel}</p>
                <ul className="space-y-3">
                  {d.ai.genericSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/70">
                      <XCircle className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#B5541B]/15 border border-[#B5541B]/40 rounded-2xl p-6" data-testid="ai-resiax-panel">
                <p className="text-xs font-bold uppercase tracking-wide text-[#D4722F] mb-4">{d.ai.resiaxLabel}</p>
                <ul className="space-y-3">
                  {d.ai.resiaxSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/90">
                      <CheckCircle className="w-4 h-4 text-[#D4722F] mt-0.5 shrink-0" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-xs text-white/50 leading-relaxed max-w-3xl" data-testid="ai-boundaries-note">{d.ai.boundaries}</p>
          </div>
        </section>

        {/* 8. Support Circle (condensed from the support proposition) */}
        <section id="support" className="py-16 lg:py-20 bg-[#FFF8F5] scroll-mt-20" data-testid="residents-support">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <span className="inline-block bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wide px-4 py-1.5 rounded-full mb-4" data-testid="residents-support-pilot-badge">
                {s.supportCircle.badge}
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-3">Bring the right people in without handing over your account</h2>
              <p className="text-base text-[#3B3535]/70 max-w-2xl mx-auto">{s.supportCircle.description}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2.5 mb-8">
              {s.supportCircle.people.map((p) => (
                <span key={p.title} data-testid={`residents-support-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="inline-flex items-center gap-2 bg-white border border-[#B5541B]/15 rounded-full px-4 py-2 text-sm text-[#3B3535]/85">
                  <span className="text-[#B5541B]">{getIcon(p.icon)}</span>
                  {p.title}
                </span>
              ))}
            </div>
            <div className="bg-[#1F1E1E] rounded-2xl p-6 sm:p-8 text-center mb-6" data-testid="residents-support-key-wording">
              <p className="text-white font-semibold text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">{s.supportCircle.keyWording}</p>
            </div>
            <div className="bg-[#F0F9F0] rounded-2xl p-6 border border-green-200/50 mb-5">
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {s.control.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-green-800/90 leading-relaxed">
                    <Shield className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-sm text-[#3B3535]/70 leading-relaxed max-w-3xl mx-auto text-center" data-testid="residents-specialist-note">
              {s.specialist.body}
            </p>
            <p className="text-xs text-[#3B3535]/55 mt-4 text-center italic max-w-2xl mx-auto">{s.associations.paragraphs[0]}</p>
          </div>
        </section>

        {/* 9. Connected channels direction */}
        <section className="py-14 bg-white" data-testid="residents-channels">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#FFF8F5] rounded-2xl p-7 border border-[#B5541B]/10 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] shrink-0">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#1F1E1E] mb-1.5">{d.channels.heading}</h2>
                <p className="text-sm text-[#3B3535]/75 leading-relaxed">{d.channels.body}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 10. Rights/compliance bridge */}
        <section className="py-14 bg-[#FFF8F5]" data-testid="residents-compliance-bridge">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-[#1F1E1E] mb-3">{d.complianceBridge.heading}</h2>
            <p className="text-base text-[#3B3535]/70 max-w-2xl mx-auto mb-6">{d.complianceBridge.body}</p>
            <Button asChild variant="outline" data-testid="residents-compliance-cta" className="border-2 border-[#B5541B] text-[#B5541B] hover:bg-[#B5541B]/10 font-semibold h-auto px-7 py-5 rounded-full">
              <Link to="/compliance">
                {d.complianceBridge.ctaLabel}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* 11. Early-access CTA */}
        <section className="py-16 bg-white" data-testid="residents-cta">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-4">{d.cta.heading}</h2>
            <p className="text-[#3B3535]/70 mb-8 max-w-xl mx-auto">{d.cta.description}</p>
            <Button
              asChild
              data-testid="residents-final-cta"
              className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Link to="/resident-pilot#register">
                {d.cta.primaryLabel}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
